import React, { useState, useEffect } from 'react';
import { MapPin, Route, Award, ChevronRight, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface WelcomeSheetProps {
  onExplore: () => void;
  onLogin: () => void;
}

const STORAGE_KEY = 'fricamp_welcome_shown';

const WelcomeSheet: React.FC<WelcomeSheetProps> = ({ onExplore, onLogin }) => {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (localStorage.getItem(STORAGE_KEY)) {
      setDismissed(true);
      return;
    }
    localStorage.setItem(STORAGE_KEY, '1');
    // Small delay for mount animation
    const timer = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  if (dismissed) return null;

  const handleDismiss = () => {
    setVisible(false);
    setTimeout(() => setDismissed(true), 300);
  };

  const cards = [
    { icon: MapPin, title: t('welcome_sheet.card1_title'), desc: t('welcome_sheet.card1_desc'), color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
    { icon: Route, title: t('welcome_sheet.card2_title'), desc: t('welcome_sheet.card2_desc'), color: 'text-blue-400', bg: 'bg-blue-500/10' },
    { icon: Award, title: t('welcome_sheet.card3_title'), desc: t('welcome_sheet.card3_desc'), color: 'text-amber-400', bg: 'bg-amber-500/10' },
  ];

  return (
    <div className={`fixed inset-0 z-[9990] pointer-events-none transition-opacity duration-300 ${visible ? 'opacity-100' : 'opacity-0'}`}>
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 pointer-events-auto"
        onClick={() => { handleDismiss(); onExplore(); }}
      />

      {/* Sheet */}
      <div className={`absolute bottom-0 left-0 right-0 pointer-events-auto bg-[var(--bg-surface)] rounded-t-panel border-t border-stone-700/50 transition-transform duration-500 ease-out ${visible ? 'translate-y-0' : 'translate-y-full'}`} style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
        {/* Drag handle */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 rounded-full bg-stone-600" />
        </div>

        {/* Close button */}
        <button
          onClick={() => { handleDismiss(); onExplore(); }}
          className="absolute top-3 right-4 p-2.5 min-w-[44px] min-h-[44px] flex items-center justify-center text-[var(--text-muted)] hover:text-[var(--text-secondary)] transition-colors"
        >
          <X size={18} />
        </button>

        <div className="px-5 pb-6 pt-2 max-w-lg mx-auto">
          {/* Cards */}
          <div className="flex gap-3 overflow-x-auto pb-4 -mx-1 px-1 scrollbar-hide" style={{ overscrollBehavior: 'contain' }}>
            {cards.map((card, i) => (
              <div key={i} className="flex-shrink-0 w-[220px] bg-stone-800/60 border border-stone-700/40 rounded-2xl p-4 space-y-2">
                <div className={`w-9 h-9 ${card.bg} rounded-xl flex items-center justify-center`}>
                  <card.icon size={18} className={card.color} />
                </div>
                <h3 className="text-tiny font-black text-[var(--text-primary)] uppercase tracking-wider">{card.title}</h3>
                <p className="text-2xs text-[var(--text-secondary)] leading-relaxed">{card.desc}</p>
              </div>
            ))}
          </div>

          {/* CTA */}
          <button
            onClick={() => { handleDismiss(); onExplore(); }}
            className="w-full bg-emerald-600 hover:bg-emerald-500 text-white py-3.5 rounded-2xl text-tiny font-black uppercase tracking-widest transition-all flex items-center justify-center gap-2"
          >
            {t('welcome_sheet.explore_cta')}
            <ChevronRight size={14} />
          </button>

          {/* Login link */}
          <div className="text-center mt-3">
            <span className="text-tiny text-[var(--text-muted)]">{t('welcome_sheet.have_account')} </span>
            <button
              onClick={() => { handleDismiss(); onLogin(); }}
              className="text-tiny py-2 font-bold text-emerald-400 hover:text-emerald-300 transition-colors"
            >
              {t('welcome_sheet.login_link')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WelcomeSheet;
